'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2Icon, Trash2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import StepUpVerificationFields from '@/components/dialog/settings/step-up-verification-fields'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { useAuth } from '@/features/auth/hooks'
import { getStepUpMode, pickStepUpVerification } from '@/lib/step-up'
import { toast } from '@/lib/toast'
import { useDeletePasskey, useGet2FABackupCodes } from '@/service/user-profile.hook'
import { PasskeyInfo, SensitiveAction } from '@/types/generated/common/auth'

type DeletePasskeyFormData = {
  currentPassword?: string
  totpCode?: string
  backupCode?: string
  emailCode?: string
}

// Verification IS the confirmation here — no separate "are you sure?" step, so a
// plain Dialog rather than an AlertDialog.
export default function DeletePasskeyDialog({ passkey }: { passkey: PasskeyInfo }) {
  const t = useTranslations('settings')
  const { hasPassword } = useAuth()
  const [isOpen, setIsOpen] = useState(false)

  // Cached React Query — the parent already subscribes to this same key.
  const { data: backupCodes } = useGet2FABackupCodes({}, { treatMissingAsDisabled: true })
  const is2faEnabled = (backupCodes?.backupCodes.length ?? 0) > 0

  const schema = useMemo(
    () =>
      z.object({
        currentPassword: z.string().optional(),
        totpCode: z.string().optional(),
        backupCode: z.string().optional(),
        emailCode: z.string().optional(),
      }),
    []
  )
  const form = useForm<DeletePasskeyFormData>({ resolver: zodResolver(schema) })

  const { mutateAsync: deletePasskey, isPending } = useDeletePasskey({
    // The hook already invalidates the passkey list on success.
    onSuccess: () => {
      toast.success(t('passkeyDeletedSuccess'))
      form.reset()
      setIsOpen(false)
    },
    onError: (error) => {
      toast.error(error.message || t('passkeyDeleteFailed'))
    },
  })

  const onSubmit = async (data: DeletePasskeyFormData) => {
    await deletePasskey({
      credentialId: passkey.credentialId,
      ...(data.currentPassword ? { currentPassword: data.currentPassword } : {}),
      ...pickStepUpVerification(data),
    })
  }

  const mode = getStepUpMode(is2faEnabled)
  const verificationReady =
    mode === 'totp'
      ? Boolean(form.watch('totpCode')) || Boolean(form.watch('backupCode'))
      : form.watch('emailCode')?.length === 6

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) form.reset()
      }}
    >
      <DialogTrigger asChild>
        <Button
          aria-label={t('deletePasskey')}
          variant="ghost"
          size="icon"
          className="hover:bg-foreground/5 bg-transparent"
        >
          <Trash2Icon className="text-destructive size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle className="flex flex-col gap-2">
          {t('deletePasskey')}
          <p className="text-sm font-normal tracking-tight text-white/60">
            {t('deletePasskeyDescription')}
          </p>
        </DialogTitle>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4 flex w-full flex-col gap-4">
          <StepUpVerificationFields
            form={form}
            hasPassword={hasPassword}
            totpEnabled={is2faEnabled}
            action={SensitiveAction.SENSITIVE_ACTION_DELETE_PASSKEY}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={
              isPending ||
              form.formState.isSubmitting ||
              (hasPassword && !form.watch('currentPassword')) ||
              !verificationReady
            }
          >
            {isPending ? <Loader2Icon className="size-3.5 animate-spin" /> : t('deletePasskey')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
